<template>
	<div
		v-if="!hasNoResults"
		:class="{
			'user-assign-select-inline-list--disabled': !isModifyEnabled,
		}"
	>
		<SuiSelectListHelpers
			v-if="!isSingleUserAssign && !isRoleFilterActive"
			class="user-assign-select-inline-list__select-all-wrapper"
		>
			<SuiSelectListHelpersSelectAll
				:checked="isAllUsersSelected"
				:select-all-text="$t('select_list_users_groups_select_all')"
				:aria-label="$t('user_assign_select_all_label')"
				@input="$emit('select-all', $event)"
			/>

			<div class="user-assign-select-inline-list__actions">
				<SuiSelectListHelpersAssignTo
					v-if="shouldShowAssignToMe"
					:url="currentUser?.profilePicture?.mini"
					:initials="currentUser?.initials ?? ''"
					:color="currentUser?.hslColor ?? ''"
					:tooltip="$t('user_assign_select_assign_to_me')"
					@click="handleAssignToMe"
				/>

				<SuiSelectListHelpersDropdown
					v-if="userGroupList.length > 0"
					:value="selectedUserGroup"
					:groups="userGroupList"
					:loading="loadingUsersAndGroups"
					:loading-label="$t('spa__general.loading')"
					:aria-label="$t('select_list_users_groups_groups')"
					:popover-props="{
						placement: 'bottom-end',
						distance: 50,
						skidding: 120,
						referenceNode: headerElement,
					}"
					:hide-on-trigger-invisible="false"
					@toggle-on="$emit('select-user-group', $event)"
					@toggle-off="$emit('deselect-user-group', $event)"
				/>
			</div>
		</SuiSelectListHelpers>

		<UserAssignSelectInlineListGroup
			v-for="group in userGroupListToDisplay"
			:key="group.id"
			:group="group"
			:is-closed="closedGroups.includes(group.id)"
			:selected-users="selectedUsers"
			:selected-roles="selectedRoles"
			:is-single-user-assign="isSingleUserAssign"
			:task-users-availability="taskUsersAvailability"
			:active-role-filter-id="activeRoleFilterId"
			@toggle-group-dropdown="
				(target, group) => $emit('toggle-group-dropdown', target, group)
			"
			@toggle-all-users-selected="
				(group, value) =>
					$emit('toggle-all-users-selected', group, value)
			"
			@user-clicked="(userId) => $emit('user-clicked', userId)"
			@role-clicked="(role) => $emit('role-clicked', role)"
		/>

		<UserAssignSelectListSkeleton
			v-if="loadingUsersAndGroups"
			show-group-wrapper
		/>
	</div>
	<UserAssignSelectListSkeleton
		v-if="hasNoResults && loadingUsersAndGroups"
		show-group-wrapper
	/>
	<SuiSelectListEmpty v-else-if="hasNoResults && !loadingUsersAndGroups">
		{{ $t('search.noResults') }}
	</SuiSelectListEmpty>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiSelectListHelpers,
	SuiSelectListHelpersSelectAll,
	SuiSelectListHelpersAssignTo,
	SuiSelectListHelpersDropdown,
	SuiSelectListEmpty,
	type SuiSelectListGroupType,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import {
	type User,
	type UserAssignSelectUserFragment,
	type UserAssignSelectInlineAvailabilityQuery,
} from '@/generated/graphql';
import { Setting } from '@/constants/settings';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { useCurrentUser } from '@/features/Auth';
import UserAssignSelectInlineListGroup from './UserAssignSelectInlineListGroup.vue';
import UserAssignSelectListSkeleton from '../../UserAssignSelect/List/UserAssignSelectListSkeleton.vue';
import {
	type UserAssignGroup,
	type UserAssignSelectRole,
} from '../../../types';

export interface UserAssignSelectInlineListProps {
	filteredUserList: UserAssignSelectUserFragment[];
	isModifyEnabled: boolean;
	isSingleUserAssign: boolean;
	isAllUsersSelected: boolean;
	selectedUsers: Array<User['id']>;
	selectedRoles: UserAssignSelectRole[];
	userGroupList: SuiSelectListGroupType[];
	selectedUserGroup: SuiSelectListItemsAndGroupsValueType;
	loadingUsersAndGroups: boolean;
	userGroupListToDisplay: UserAssignGroup[];
	closedGroups?: UserAssignGroup['id'][];
	headerElement: () => Element | null;
	taskUsersAvailability?: UserAssignSelectInlineAvailabilityQuery['TaskUsersAvailability'];
	activeRoleFilterId?: UserAssignSelectRole['id'] | null;
}

const {
	closedGroups = [],
	taskUsersAvailability = [],
	activeRoleFilterId = null,
	filteredUserList,
	userGroupListToDisplay,
	selectedUsers,
} = defineProps<UserAssignSelectInlineListProps>();

const emit = defineEmits<{
	'select-all': [value: boolean];
	'select-user-group': [groupId: string];
	'deselect-user-group': [groupId: string];
	'toggle-group-dropdown': [target: HTMLElement, group: UserAssignGroup];
	'toggle-all-users-selected': [group: UserAssignGroup, value: boolean];
	'user-clicked': [userId: User['id']];
	'role-clicked': [role: UserAssignSelectRole];
}>();

const [assignRolesToTasksSetting] = useBooleanSetting(
	Setting.ASSIGN_ROLES_TO_TASKS
);

const { currentUser } = useCurrentUser();

const hasNoResults = computed(() => {
	if (assignRolesToTasksSetting.value) {
		return userGroupListToDisplay.length === 0;
	}
	return filteredUserList.length === 0;
});

const isRoleFilterActive = computed(() => {
	return activeRoleFilterId !== null;
});

const isCurrentUserSelected = computed(() => {
	if (!currentUser.value?.id) {
		return false;
	}
	return selectedUsers.includes(currentUser.value.id);
});

const shouldShowAssignToMe = computed(() => !isCurrentUserSelected.value);

const handleAssignToMe = () => {
	if (currentUser.value?.id) {
		emit('user-clicked', currentUser.value.id);
	}
};
</script>

<style scoped lang="scss">
.user-assign-select-inline-list {
	&--disabled {
		pointer-events: none;
	}

	&__select-all-wrapper {
		--sui-toggle-button-padding: 0.5rem;

		min-height: 5rem;
		padding: 1rem 1.5rem 1rem 2rem;
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
}
</style>
